import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, Building2, Sparkles, Receipt, UserCheck, Globe, ChevronDown } from "lucide-react";
import { useLang, LANGUAGES } from "../i18n";
import { useAuth } from "../context/AuthContext";
import AiAssistant from "./AiAssistant";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";

function Header() {
  const { t, lang, setLang } = useLang();
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const current = LANGUAGES.find((l) => l.code === lang);

  const switchRole = async (role) => { await updateProfile({ role }); navigate("/home"); };
  const switchLang = async (code) => { setLang(code); if (user) await updateProfile({ language: code }); };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-slate-200/80 px-4 py-3 flex items-center justify-between max-w-md md:max-w-4xl mx-auto">
      <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/home")}>
        <div className="w-8 h-8 rounded-lg bg-terracotta flex items-center justify-center text-white"><Home className="w-4.5 h-4.5" strokeWidth={2.5} /></div>
        <span className="font-head font-extrabold text-lg text-slate-900">{t("app_name")}</span>
      </div>
      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger data-testid="language-switcher-btn" className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-700">
            <Globe className="w-3.5 h-3.5" /> {current?.native || "भाषा"}<ChevronDown className="w-3 h-3" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
            {LANGUAGES.map((l) => (
              <DropdownMenuItem key={l.code} onClick={() => switchLang(l.code)} data-testid={`lang-opt-${l.code}`} className="cursor-pointer">
                <span className="font-medium">{l.native}</span><span className="text-slate-400 ml-2 text-xs">{l.en}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {user && (
          <div className="flex rounded-full bg-slate-100 p-0.5 text-xs font-semibold">
            <button onClick={() => switchRole("owner")} data-testid="role-toggle-landlord-btn" className={`px-2.5 py-1 rounded-full transition ${user.role === "owner" ? "bg-terracotta text-white" : "text-slate-500"}`}>{t("landlord_mode")}</button>
            <button onClick={() => switchRole("tenant")} data-testid="role-toggle-tenant-btn" className={`px-2.5 py-1 rounded-full transition ${user.role === "tenant" ? "bg-terracotta text-white" : "text-slate-500"}`}>{t("tenant_mode")}</button>
          </div>
        )}
      </div>
    </header>
  );
}

const NAV = [
  { to: "/home", icon: Home, key: "nav_home", id: "nav-home-tab" },
  { to: "/properties", icon: Building2, key: "nav_properties", id: "nav-properties-tab" },
  { to: "/payments", icon: Receipt, key: "nav_payments", id: "nav-payments-tab" },
  { to: "/profile", icon: UserCheck, key: "nav_profile", id: "nav-profile-tab" },
];

export default function Layout({ children }) {
  const { t } = useLang();
  const [aiOpen, setAiOpen] = useState(false);
  return (
    <div className="max-w-md md:max-w-4xl mx-auto min-h-screen bg-sand pb-24 relative">
      <Header />
      <main className="px-4 py-4">{children}</main>

      <button
        onClick={() => setAiOpen(true)}
        data-testid="floating-ai-assistant-btn"
        className="fixed bottom-20 right-4 z-40 bg-gradient-to-r from-terracotta to-[#E05A47] text-white px-4 py-3 rounded-full shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center gap-2 text-sm font-semibold max-w-md md:max-w-4xl"
        style={{ right: "max(1rem, calc(50% - 13.5rem))" }}
      >
        <Sparkles className="w-4 h-4" /> {t("ask_sathi")}
      </button>

      <AiAssistant open={aiOpen} setOpen={setAiOpen} />

      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 px-2 py-1.5 shadow-lg max-w-md md:max-w-4xl mx-auto flex items-center justify-around">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={n.id}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl text-[11px] font-semibold transition ${isActive ? "text-terracotta" : "text-slate-400"}`}>
            <n.icon className="w-5 h-5" /> {t(n.key)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
